import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS, colorWithAlpha } from '../../rendering/Renderer.js';
import { VortexHubTutorialState, VortexHubTutorialRenderOptions } from './VortexHubTutorialAdapter.js';

type VHState = VortexHubTutorialState;
type VHRender = VortexHubTutorialRenderOptions;

const scenes: TutorialScene[] = [
    // Scene 1: Extra digit
    {
        gameState: { hubData: '' } as VHState,
        renderOptions: { mode: 'none' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'Vortex Grids have\none extra digit', cell: 'C0', anchor: 'center' },
            { type: 'text-label', text: 'They may be unsolvable\nthe regular way', cell: 'D3', anchor: 'center' },
            { type: 'highlight', cells: ['C4', 'F2', 'A3', 'D1'], color: colorWithAlpha('teal', 0.2) },
            { type: 'pulse', cell: 'C4', color: COLORS.teal },
        ],
    },

    // Scene 2: Vortex Hub intro — two linked gate pairs
    {
        gameState: { hubData: 'C2D2D3D4,A1A0B0C0' } as VHState,
        renderOptions: { mode: 'idle' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'This is the Vortex Hub', cell: 'D1', anchor: 'center' },
            { type: 'text-label', text: 'Stuff teleports\nfrom a gate to its pair', cell: 'C5', anchor: 'center' },
            { type: 'pulse', cell: 'C2', color: COLORS.purple },
            { type: 'pulse', cell: 'D4', color: COLORS.purple },
            { type: 'pulse', cell: 'A1', color: COLORS.purple },
            { type: 'pulse', cell: 'C0', color: COLORS.purple },
        ],
    },

    // Scene 3: Create a pair — click an empty cell, then another (animate first → second)
    {
        gameState: { hubData: 'C2D2D3D4,A1=C0' } as VHState,
        renderOptions: { mode: 'idle' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'Click an empty cell\nto place a gate', cell: 'E0', anchor: 'center' },
            { type: 'text-label', text: 'Click another empty cell\nto complete the pair', cell: 'D3', anchor: 'center' },
        ],
        animate: {
            interval: 1300,
            loop: true,
            frames: [
                { hubData: 'C2D2D3D4' },
                { hubData: 'C2D2D3D4', pendingCreate: 'A1' },
                { hubData: 'C2D2D3D4,A1=C0' },
            ] as VHState[],
        },
    },

    // Scene 4: Link a pair — click a gate, trace cells to its partner
    {
        gameState: { hubData: 'C2D2D3D4,A1=C0' } as VHState,
        renderOptions: { mode: 'idle' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'Click a gate, then trace\ncells to its partner', cell: 'D3', anchor: 'center' },
            { type: 'text-label', text: 'Every pair must\nbe linked eventually', cell: 'C5', anchor: 'center' },
        ],
        animate: {
            interval: 1200,
            loop: true,
            frames: [
                { hubData: 'C2D2D3D4,A1=C0', buildPath: ['A1'] },
                { hubData: 'C2D2D3D4,A1=C0', buildPath: ['A1', 'A0'] },
                { hubData: 'C2D2D3D4,A1=C0', buildPath: ['A1', 'A0', 'B0'] },
                { hubData: 'C2D2D3D4,A1A0B0C0' },
            ] as VHState[],
        },
    },

    // Scene 5: Path constraint — a link splits the grid, digits must stay connected
    {
        gameState: { hubData: 'C2D2D3D4,A1A0B0C0,E0E1E2E3F3' } as VHState,
        renderOptions: { mode: 'idle' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'A link splits the grid\nDigits must stay connected', cell: 'D5', anchor: 'center', color: COLORS.red },
        ],
    },

    // Scene 6: Remove a pair — right-click a gate, then confirm
    {
        gameState: { hubData: 'C2D2D3D4,A1A0B0C0' } as VHState,
        renderOptions: { mode: 'gates-pending' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'Delete Gates by\nclicking the button', cell: 'E0', anchor: 'center', color: COLORS.amber },
            { type: 'text-label', text: 'You can also\nright-click on them', cell: 'E5', anchor: 'center', color: COLORS.amber },
        ],
        animate: {
            interval: 2000,
            loop: true,
            frames: [
                // Frame 1: the pair, idle
                { hubData: 'C2D2D3D4,A1A0B0C0' },
                // Frame 2: right-clicked pair awaiting confirmation (red)
                { hubData: 'C2D2D3D4,A1A0B0C0', pendingDelete: { clickedGateKey: 'A1', pairedGateKey: 'C0' } },
                // Frame 3: confirmed — the pair is gone
                { hubData: 'C2D2D3D4' },
            ] as VHState[],
        },
    },

    // Scene 7: Closing
    {
        gameState: { hubData: 'C2D2D3D4,A1A0B0C0' } as VHState,
        renderOptions: { mode: 'none' } as VHRender,
        annotations: [
            { type: 'text-label', text: 'See game tutorials\nto see the gates in action', cell: 'D1', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Gates don\'t count in\nyour score, use them!', cell: 'C5', anchor: 'center', color: COLORS.teal },
        ],
    },
];

/**
 * Vortex Hub Basics tutorial: 7 scenes on a 6×6 vortex grid with digits 0-3.
 * Introduces the Vortex Hub, gate placement, linking, and constraints.
 */
export const VORTEX_HUB_TUTORIAL: TutorialDefinition = {
    gameType: 'vortex-hub',
    gridCode: '6/0-3:0C4,1F2,2A3,3D1',
    scenes,
};
